import React, { useState, useEffect } from 'react';
import { WorkoutType, CompetitionCategory, Workout, CreateWorkoutInput } from '../types/workout';

interface WorkoutFormProps {
  workout?: Workout;
  onSubmit: (workout: CreateWorkoutInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const WorkoutForm: React.FC<WorkoutFormProps> = ({ workout, onSubmit, onCancel, loading = false }) => {
  const [name, setName] = useState(workout?.name || '');
  const [description, setDescription] = useState(workout?.description || '');
  const [type, setType] = useState<WorkoutType>(workout?.type || WorkoutType.FOR_TIME);
  const [items, setItems] = useState<string[]>(workout?.items || ['']);
  const [timeCap, setTimeCap] = useState(workout?.timeCap || 0);
  const [category, setCategory] = useState<CompetitionCategory>(workout?.category || CompetitionCategory.RX);
  const [error, setError] = useState<string | null>(null);

  const addItem = () => {
    setItems([...items, '']);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    const filteredItems = items.filter(item => item.trim() !== '');
    if (filteredItems.length === 0) {
      setError('Please add at least one exercise');
      return;
    }

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        type,
        items: filteredItems,
        timeCap,
        category,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save workout');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Workout Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Workout 1, Fran, Hero WOD"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={2}
          placeholder="e.g., 21-15-9 Thrusters and Pull-ups"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Workout Type *
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as WorkoutType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={WorkoutType.FOR_TIME}>For Time</option>
            <option value={WorkoutType.AMRAP}>AMRAP</option>
            <option value={WorkoutType.EMOM}>EMOM</option>
            <option value={WorkoutType.MAX_TIME}>Max Time</option>
            <option value={WorkoutType.MAX_WEIGHT}>Max Weight</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CompetitionCategory)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={CompetitionCategory.RX}>RX</option>
            <option value={CompetitionCategory.SCALED}>Scaled</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Cap (minutes) *
        </label>
        <input
          type="number"
          value={timeCap}
          onChange={(e) => setTimeCap(parseInt(e.target.value) || 0)}
          required
          min="0"
          placeholder="e.g., 15"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Exercises/Movements *
          </label>
          <button
            type="button"
            onClick={addItem}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            + Add Exercise
          </button>
        </div>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                placeholder={`Exercise ${index + 1} (e.g., Thrusters 95/65 lbs)`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : workout ? 'Update Workout' : 'Add Workout'}
        </button>
      </div>
    </form>
  );
};

export default WorkoutForm;
